import { useCallback, useState } from 'react';
import useMutation from 'src/hooks/useMutation';
import {
  useGetNotificationQuery,
  useUpdateNotificationMutation,
  type NotificationListResponse,
  type NotificationCountsResponse
} from 'src/newServices/notifications';
import { PAGE_SIZE } from '../constants';
import { ErrorContext } from 'src/utils/errorMappings';

import { NotificationTab } from '../types';

type UseNotificationsProps = {
  open?: boolean;
};

function useNotifications({ open = false }: UseNotificationsProps = {}) {
  const [activeTab, setActiveTab] = useState<NotificationTab>(NotificationTab.All);
  const [cursor, setCursor] = useState<string | null>(null);

  const isActiveTabUnread = activeTab === NotificationTab.Unread;

  const [update] = useMutation({
    api: useUpdateNotificationMutation,
    errorContext: ErrorContext.NOTIFICATIONS
  });

  const { data: listData, isLoading, isFetching, refetch } = useGetNotificationQuery({
    params: {
      type: 'list',
      ps: PAGE_SIZE,
      ...(isActiveTabUnread ? { is_read: false } : {}),
      ...(cursor ? { cursor } : {})
    }
  } as any, { skip: !open }) as { data: NotificationListResponse | undefined; isLoading: boolean; isFetching: boolean; refetch: () => void };

  const { data: countsData } = useGetNotificationQuery({
    params: { type: 'counts' }
  } as any) as { data: NotificationCountsResponse | undefined };

  const notifications = listData?.data ?? [];
  const totalCount = countsData?.data?.[0]?.count ?? 0;
  const unreadCount = countsData?.data?.[0]?.unread_count ?? 0;
  const hasMore = (listData?.lastFetchSize ?? 0) === PAGE_SIZE;

  const handleTabChange = useCallback((tab: NotificationTab) => {
    setActiveTab(tab);
    setCursor(null);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || !listData?.nextCursor) return;
    setCursor(listData.nextCursor);
  }, [hasMore, listData?.nextCursor]);

  const handleMarkAllAsRead = () => {
    update({ params: { params: { action: 'mark_all_read' } } });
  };

  const handleDismissAll = () => {
    update({ params: { params: { action: 'dismiss_all' } } });
  };

  const handleDismiss = (id: number) => {
    update({ params: { params: { action: 'dismiss', notification_id: id } } });
  };

  const handleMarkReadStatus = (id: number, isRead: boolean) => {
    update({ params: { params: { action: 'read_status', notification_id: id, is_read: isRead } } });
  };

  return {
    activeTab,
    setActiveTab: handleTabChange,
    notifications,
    totalCount,
    unreadCount,
    hasMore,
    isLoading,
    isLoadingMore: isFetching,
    handleLoadMore,
    handleDismissAll,
    handleMarkAllAsRead,
    handleDismiss,
    handleMarkReadStatus
  };
}

export default useNotifications;
